/**
 * 本地草稿持久化（localStorage）
 * 目标：刷新页面不丢失“标书解析/目录/正文”关键中间结果，方便调试与续写。
 *
 * 注意：localStorage 容量有限（通常 5-10MB），大正文可能触发 QUOTA_EXCEEDED_ERR。
 * 这里做了 try/catch，失败时不影响主流程。
 */

import type { AppState, OutlineItem } from '../types';

const DRAFT_KEY = 'biao:draft:v1';
const CONTENT_BY_ID_KEY = 'biao:contentById:v1';

export type DraftState = Pick<
  AppState,
  'projectId' | 'currentStep' | 'maxReachedStep' | 'projectConfig' | 'fileContent' | 'projectOverview' | 'techRequirements' | 'outlineRequirements' | 'disqualification' | 'quotation' | 'templates' | 'quotationData' | 'outlineData' | 'selectedChapter' | 'stepsCompleted'
>;

export type ContentById = Record<string, string>; // 章节id -> content

const safeJsonParse = <T,>(raw: string | null): T | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

export const draftStorage = {
  loadDraft(): Partial<DraftState> | null {
    return safeJsonParse<Partial<DraftState>>(localStorage.getItem(DRAFT_KEY));
  },

  saveDraft(partial: Partial<DraftState>) {
    try {
      const prev = safeJsonParse<Partial<DraftState>>(localStorage.getItem(DRAFT_KEY)) || {};
      const next = { ...prev, ...partial };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(next));
    } catch (e) {
      console.warn('保存草稿失败（可能是 localStorage 空间不足）:', e);
    }
  },

  clearAll() {
    // 清空项目相关的 localStorage，但保留认证 token
    try {
      // 保存认证相关的数据
      const accessToken = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');
      const user = localStorage.getItem('user');
      
      // 清空所有
      localStorage.clear();
      
      // 恢复认证数据
      if (accessToken) localStorage.setItem('access_token', accessToken);
      if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
      if (user) localStorage.setItem('user', user);
      
      console.log('✅ 已清空项目数据（保留认证信息）');
    } catch (e) {
      console.warn('清空 localStorage 失败:', e);
    }
  },

  loadContentById(): ContentById {
    return safeJsonParse<ContentById>(localStorage.getItem(CONTENT_BY_ID_KEY)) || {};
  },

  saveContentById(contentById: ContentById) {
    try {
      localStorage.setItem(CONTENT_BY_ID_KEY, JSON.stringify(contentById));
    } catch (e) {
      console.warn('保存正文内容失败（可能是 localStorage 空间不足）:', e);
    }
  },

  upsertChapterContent(chapterId: string, content: string) {
    try {
      const map = draftStorage.loadContentById();
      map[chapterId] = content;
      draftStorage.saveContentById(map);
    } catch (e) {
      console.warn('保存章节内容失败:', e);
    }
  },
  
  /**
   * 清空所有章节内容
   */
  clearAllContent() {
    try {
      localStorage.removeItem(CONTENT_BY_ID_KEY);
    } catch (e) {
      console.warn('清空章节内容失败:', e);
    }
  },

  /**
   * 按当前 outline 的叶子节点过滤 contentById，避免目录变更后错误回填。
   */
  filterContentByOutlineLeaves(outline: OutlineItem[]): ContentById {
    const map = draftStorage.loadContentById();
    const leafIds = new Set<string>();
    const walk = (items: OutlineItem[]) => {
      items.forEach((it) => {
        if (!it.children || it.children.length === 0) {
          leafIds.add(it.id);
          return;
        }
        walk(it.children);
      });
    };
    walk(outline);

    const filtered: ContentById = {};
    Object.keys(map).forEach((id) => {
      if (leafIds.has(id)) filtered[id] = map[id];
    });
    return filtered;
  },
};


